import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { Dialog } from '@openng/optimus-ui/dialog';

import { APP_DIALOG_TITLE_ID } from '../dialog/app-dialog.model';

/** The number of modals created so far, which makes each modal's own title id unique. */
let createdModals = 0;

@Component({
	selector: 'app-modal',
	templateUrl: './modal.component.html',
	imports: [NgTemplateOutlet, Dialog],
	host: { '(document:keydown.escape)': 'onEscape()' },
})
export class ModalComponent {
	readonly closeVisible = input<boolean>(false);
	readonly wide = input<boolean>(false);
	readonly narrow = input<boolean>(false);
	readonly standaloneDialog = input<boolean>(false);
	readonly centerButton = input<boolean>(false);
	readonly showHeader = input<boolean>(true);
	readonly title = input<string>();
	readonly subtitle = input<string>();
	/** The id of the element that names a standalone dialog whose header shows no title. */
	readonly labelledBy = input<string>();
	/** When false, backdrop click and Escape no longer close the modal (e.g. forms that would lose data). The X still closes it. */
	readonly dismissable = input<boolean>(true);
	/** Emits when the user closes the modal: the close icon, a backdrop click or Escape. */
	readonly closed = output<void>();

	private readonly dialogTitleId = inject(APP_DIALOG_TITLE_ID, { optional: true });
	private readonly ownTitleId = `app-modal-title-${++createdModals}`;

	/**
	 * The id of the title heading, which names the dialog: its own dialog's in a standalone dialog, else the id of the
	 * dialog `AppDialogService` opened it in.
	 */
	protected readonly titleId = computed(() =>
		this.standaloneDialog() ? this.ownTitleId : (this.dialogTitleId ?? this.ownTitleId)
	);

	/** The id that names the standalone dialog: its title while the header shows one, else `labelledBy`. */
	protected readonly dialogLabelledBy = computed(() =>
		this.showHeader() && this.title() ? this.titleId() : (this.labelledBy() ?? this.titleId())
	);

	protected readonly widthClasses = computed(() => {
		if (this.wide()) {
			return 'sm:w-[500px] md:w-[700px] lg:w-[800px] xl:w-[1200px] 2xl:w-[1200px]';
		} else if (this.narrow()) {
			return 'sm:w-[600px]';
		}
		return '';
	});

	// Backdrop click and Escape close the modal only when it offers a close affordance and is dismissable.
	protected readonly canDismiss = computed(() => this.closeVisible() && this.dismissable());

	protected handleClose() {
		this.emitClosed();
	}

	/** The library dialog hides itself on a backdrop click, which it allows only while `canDismiss()`. */
	protected onDialogVisibleChange(visible: boolean): void {
		if (!visible) {
			this.emitClosed();
		}
	}

	protected onEscape() {
		if (this.canDismiss()) {
			this.emitClosed();
		}
	}

	private emitClosed(): void {
		this.closed.emit();
	}
}
